import {
  SoftwareCategorySummary,
  SoftwarePackageItem,
} from 'app/api/services/scanVaultSoftware';

export const OPERATING_SYSTEM_CATEGORY = 'operating_system';

const categoryOrder = [
  'operating_system',
  'os',
  'technology',
  'application',
  'library',
  'libraries',
  'ai_ml',
  'runtime',
  'other',
];

const categoryLabels: Record<string, string> = {
  ai_ml: 'AI & ML',
  libraries: 'Libraries',
  operating_system: 'Operating Systems',
  os: 'OS Packages',
};

const categoryIcons: Record<string, string> = {
  ai_ml: 'AIML',
  application: 'Technology',
  library: 'Library',
  libraries: 'Library',
  operating_system: 'OS',
  runtime: 'Technology',
  technology: 'Technology',
};

export const formatSoftwareLabel = (value?: string) =>
  (value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

// Operating systems use their OS family as the ecosystem.
const osFamilyLabels: Record<string, string> = {
  ubuntu: 'Ubuntu',
  debian: 'Debian',
  'amazon-linux': 'Amazon Linux',
  rhel: 'Red Hat Enterprise Linux',
  centos: 'CentOS',
  'rocky-linux': 'Rocky Linux',
  almalinux: 'AlmaLinux',
  alpine: 'Alpine Linux',
  suse: 'SUSE Linux',
  windows: 'Windows',
  macos: 'macOS',
};

const ecosystemLabels: Record<string, string> = {
  ...osFamilyLabels,
  operating_system: 'Operating System',
  os: 'OS Packages',
  deb: 'Debian Packages',
  rpm: 'RPM Packages',
  apk: 'Alpine Packages',
  npm: 'npm',
  pypi: 'PyPI',
  python: 'Python',
  go: 'Go Modules',
  gomod: 'Go Modules',
  'go-module': 'Go Modules',
  golang: 'Go Modules',
  maven: 'Maven',
  'java-archive': 'Java Archives',
  jar: 'Java Archives',
  gem: 'RubyGems',
  cargo: 'Cargo',
  'rust-crate': 'Cargo',
  nuget: 'NuGet',
  composer: 'Composer',
  'php-composer': 'Composer',
  binary: 'Binaries',
};

export const getSoftwareEcosystemLabel = (ecosystem?: string) =>
  ecosystemLabels[(ecosystem ?? '').toLowerCase()] ??
  formatSoftwareLabel(ecosystem);

// getIcon matches OS icons by their display name (Ubuntu, Amazon Linux, ...).
export const getSoftwareEcosystemIcon = (ecosystem?: string) =>
  osFamilyLabels[(ecosystem ?? '').toLowerCase()] ?? ecosystem ?? '';

// getIcon keys for technology products (ScanVault technology_key).
const technologyIcons: Record<string, string> = {
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  postgresql: 'PostgreSQL',
  redis: 'Redis',
  mongodb: 'MongoDB',
  elasticsearch: 'elasticsearch',
  cassandra: 'Cassandra',
  couchdb: 'CouchDB',
  neo4j: 'Neo4j',
  memcached: 'Memcached',
  nginx: 'Nginx',
  haproxy: 'HAProxy',
  'apache-web-server': 'Apache HTTP',
  'apache-tomcat': 'Apache Tomcat',
};

/** Brand icon for a technology product, or the generic technology icon. */
export const getSoftwareTechnologyIcon = (technologyKey?: string) =>
  technologyIcons[(technologyKey ?? '').toLowerCase()] ?? 'Technology';

const subcategoryLabels: Record<string, Record<string, string>> = {
  technology: {
    database: 'Databases',
    web_server: 'Web Servers',
    app_server: 'App Servers',
  },
  ai_ml: {
    llm_sdk: 'LLM SDKs',
    agent_framework: 'Agent Frameworks',
    ml_framework: 'ML Frameworks',
  },
  library: {
    javascript: 'JavaScript',
    python: 'Python',
    go: 'Go',
    java: 'Java',
    ruby: 'Ruby',
    rust: 'Rust',
    dotnet: '.NET',
    php: 'PHP',
    swift: 'Swift',
    elixir: 'Elixir',
    dart: 'Dart',
    cpp: 'C/C++',
  },
};

const osFamiliesWithIcons = new Set([
  'ubuntu',
  'debian',
  'amazon-linux',
  'rhel',
  'centos',
  'alpine',
  'windows',
]);

const languageIcons: Record<string, string> = {
  javascript: 'nodejs',
  python: 'python',
  java: 'java',
  go: 'golang',
  ruby: 'ruby',
};

// Subcategories other than the curated ones are ecosystems (deb, rpm, ...).
export const getSoftwareSubcategoryLabel = (
  category: string,
  subcategory?: string,
) =>
  subcategoryLabels[category.toLowerCase()]?.[
    (subcategory ?? '').toLowerCase()
  ] ?? getSoftwareEcosystemLabel(subcategory);

export const getSoftwareSubcategoryIcon = (
  category: string,
  subcategory?: string,
) => {
  switch (category.toLowerCase()) {
    case 'technology':
      return 'Technology';
    case 'ai_ml':
      return 'AIML';
    case 'library':
      return languageIcons[(subcategory ?? '').toLowerCase()] ?? 'Library';
    case OPERATING_SYSTEM_CATEGORY:
      // Families without a brand icon get the generic operating system one.
      return osFamiliesWithIcons.has((subcategory ?? '').toLowerCase())
        ? getSoftwareEcosystemIcon(subcategory)
        : 'OS';
    default:
      return getSoftwareEcosystemIcon(subcategory);
  }
};

export interface SoftwareSubcategoryEntry {
  key: string;
  resource_count: number;
  installation_count: number;
  package_count: number;
  name_count?: number;
  /** Packages filter selecting this subcategory. */
  filter: { subcategory?: string; ecosystem?: string };
}

export const getSoftwareSubcategories = (
  category: SoftwareCategorySummary,
): SoftwareSubcategoryEntry[] =>
  category.subcategories?.length
    ? category.subcategories.map(({ subcategory, ...counts }) => ({
        ...counts,
        key: subcategory,
        filter: { subcategory },
      }))
    : // Backends without subcategories group the second level by ecosystem.
      (category.ecosystems ?? []).map(({ ecosystem, ...counts }) => ({
        ...counts,
        key: ecosystem,
        filter: { ecosystem },
      }));

// Operating systems are listed per release (EOL is per release); everything
// else per package name.
export const getSoftwareEntityCount = (
  category: string,
  summary: { package_count: number; name_count?: number },
) =>
  category === OPERATING_SYSTEM_CATEGORY
    ? summary.package_count
    : summary.name_count ?? summary.package_count;

export const getSoftwareCategoryLabel = (category: string) =>
  categoryLabels[category.toLowerCase()] ?? formatSoftwareLabel(category);

export const getSoftwareCategoryIcon = (category: string) =>
  categoryIcons[category.toLowerCase()] ?? 'Packages';

export const sortSoftwareCategories = (categories: SoftwareCategorySummary[]) =>
  [...categories].sort((left, right) => {
    const leftIndex = categoryOrder.indexOf(left.category.toLowerCase());
    const rightIndex = categoryOrder.indexOf(right.category.toLowerCase());

    if (leftIndex === -1 && rightIndex === -1) {
      return left.category.localeCompare(right.category);
    }

    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });

export const formatSoftwareCount = (value?: number) =>
  (value ?? 0).toLocaleString('en-US');

// Removed the unused clouds, accountCount, environmentCount and eol row fields (never displayed).
export interface SoftwareInventoryEntityRow {
  name: string;
  /** Technology rows: the product (MySQL) and the packages behind it. */
  technologyKey?: string;
  technologyName?: string;
  packageNames?: string[];
  /** Release rows only: "Ubuntu 22.04", with the version kept for filtering. */
  displayName?: string;
  version?: string;
  eolDate?: string;
  ecosystem: string;
  lastObservedAt: string;
  deployedIn: {
    upper: string;
    lower: string;
  };
  hosts: number;
  versions: number;
}

export const formatSoftwareEOL = (eolDate?: string, now = new Date()) => {
  if (!eolDate) return { date: '-', status: 'Unknown', isPast: false };
  const date = new Date(`${eolDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return { date: '-', status: 'Unknown', isPast: false };
  }
  const isPast = date.getTime() < now.getTime();
  return {
    date: date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),
    status: isPast ? 'End of life' : 'Supported',
    isPast,
  };
};

// One row per release, from per-version package items.
export const toSoftwareReleaseRows = (
  packages: SoftwarePackageItem[],
): SoftwareInventoryEntityRow[] =>
  packages
    .map(item => ({
      name: item.name,
      displayName: [item.name, item.version].filter(Boolean).join(' '),
      version: item.version,
      eolDate: item.eol_date,
      ecosystem: item.ecosystem,
      lastObservedAt: item.last_observed_at,
      deployedIn: {
        upper: `${formatSoftwareCount(item.resource_count)} resources`,
        lower: `${formatSoftwareCount(item.installation_count)} installations`,
      },
      hosts: item.resource_count,
      versions: 1,
    }))
    .sort((left, right) =>
      left.displayName.localeCompare(right.displayName, undefined, {
        numeric: true,
      }),
    );

// Removed unused groupSoftwareInstallations; installations are rendered ungrouped in the drawer.
export const groupSoftwarePackages = (
  packages: SoftwarePackageItem[],
): SoftwareInventoryEntityRow[] => {
  const groups = new Map<
    string,
    {
      name: string;
      technologyKey?: string;
      technologyName?: string;
      packageNames: Set<string>;
      ecosystem: string;
      lastObservedAt: string;
      versions: Set<string>;
      versionCount: number;
      resourceCount: number;
      installationCount: number;
    }
  >();

  packages.forEach(item => {
    const key = `${item.category}:${item.ecosystem}:${item.name}`;
    const isTechnology =
      item.category === 'technology' && !!item.technology_key;
    const group = groups.get(key) ?? {
      name: item.name,
      technologyKey: isTechnology ? item.technology_key : undefined,
      technologyName: isTechnology ? item.technology_name : undefined,
      packageNames: new Set<string>(),
      ecosystem: item.ecosystem,
      lastObservedAt: item.last_observed_at,
      versions: new Set<string>(),
      versionCount: 0,
      resourceCount: 0,
      installationCount: 0,
    };

    if (item.version) group.versions.add(item.version);
    if (isTechnology) {
      (item.package_names?.length
        ? item.package_names
        : [item.name]
      ).forEach(name => group.packageNames.add(name));
    }
    group.versionCount += item.version_count ?? 0;
    if (item.last_observed_at > group.lastObservedAt) {
      group.lastObservedAt = item.last_observed_at;
    }
    // Name-level rows (group_by=name) arrive once per name with exact counts.
    // Per-version rows can only bound resources from below, since one
    // resource may hold several versions.
    group.resourceCount = Math.max(group.resourceCount, item.resource_count);
    group.installationCount += item.installation_count;
    groups.set(key, group);
  });

  return Array.from(groups.values())
    .map(group => ({
      name: group.name,
      ...(group.technologyKey
        ? {
            technologyKey: group.technologyKey,
            technologyName: group.technologyName || group.name,
            packageNames: Array.from(group.packageNames).sort(),
          }
        : {}),
      ecosystem: group.ecosystem,
      lastObservedAt: group.lastObservedAt,
      deployedIn: {
        upper: `${formatSoftwareCount(group.resourceCount)} resources`,
        lower: `${formatSoftwareCount(group.installationCount)} installations`,
      },
      hosts: group.resourceCount,
      versions: Math.max(group.versions.size, group.versionCount),
    }))
    .sort((left, right) =>
      (left.technologyName ?? left.name).localeCompare(
        right.technologyName ?? right.name,
      ),
    );
};
